import React, { useState, useEffect, useRef } from 'react';
import { Form, InputGroup, Dropdown, Badge, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faMapMarkerAlt, faShieldAlt } from '@fortawesome/free-solid-svg-icons';

interface AddressSuggestion {
  address: string;
  coordinates: [number, number] | null;
  confidence: number;
  source: string;
}

interface AddressSearchProps {
  value: string;
  onChange: (value: string) => void;
  onAddressSelect?: (suggestion: AddressSuggestion) => void;
  placeholder?: string;
  isRequired?: boolean;
  disabled?: boolean;
  className?: string;
  label?: string;
  error?: string;
}

const AddressSearchComponent: React.FC<AddressSearchProps> = ({
  value,
  onChange,
  onAddressSelect,
  placeholder = "Enter property address...",
  isRequired = false,
  disabled = false,
  className = "",
  label = "Address",
  error
}) => {
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Fetch address suggestions using server-side proxy
  const fetchAddressSuggestions = async (query: string): Promise<AddressSuggestion[]> => {
    if (query.length < 3) return [];
    
    try {
      setIsLoadingSuggestions(true);
      
      // Try public endpoint first (for testing/demo)
      let response = await fetch(`http://localhost:8080/api/properties/address-suggestions-public?query=${encodeURIComponent(query)}`);
      
      // If public endpoint fails, try authenticated endpoint
      if (!response.ok) {
        console.log('Public endpoint failed, trying authenticated endpoint...');
        response = await fetch(`http://localhost:8080/api/properties/address-suggestions?query=${encodeURIComponent(query)}`);
      }
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.suggestions) {
          console.log(`Received ${data.suggestions.length} address suggestions from ${data.source}`);
          return data.suggestions;
        } else {
          console.warn('Address suggestions API returned no results:', data);
          return [];
        }
      } else {
        console.error('Address suggestions API failed:', response.status, response.statusText);
        return [];
      }
    } catch (error) {
      console.error('Error fetching address suggestions:', error);
      return [];
    } finally {
      setIsLoadingSuggestions(false);
    }
  };

  // Debounced address suggestions fetch
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (value && value.length >= 3 && !disabled) {
        console.log('🔄 [ADDRESS SEARCH] Fetching suggestions for:', value);
        const suggestions = await fetchAddressSuggestions(value);
        console.log('📍 [ADDRESS SEARCH] Received suggestions:', suggestions.length);
        setSuggestions(suggestions);
        setShowSuggestions(suggestions.length > 0);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(timer);
  }, [value, disabled]);

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
        setSuggestions([]);
      }
    };

    if (showSuggestions) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showSuggestions]);

  // Handle address input changes
  const handleAddressChange = (newValue: string) => {
    console.log('Address input changed to:', newValue);
    onChange(newValue);
    
    // Show suggestions if we have meaningful input
    if (newValue.length >= 5 && newValue.includes(' ')) {
      if (!isLoadingSuggestions && !disabled) {
        setIsLoadingSuggestions(true);
      }
    } else {
      setIsLoadingSuggestions(false);
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // Handle address suggestion selection
  const handleAddressSelect = (suggestion: AddressSuggestion) => {
    console.log('🎯 [ADDRESS SELECT] Selected suggestion:', suggestion);
    
    // Set the address value
    onChange(suggestion.address);
    
    // Immediately close suggestions
    setIsLoadingSuggestions(false);
    setSuggestions([]);
    setShowSuggestions(false);
    
    // Notify parent component
    if (onAddressSelect) {
      onAddressSelect(suggestion);
    }
  };

  // Handle address input blur
  const handleAddressBlur = () => {
    // Delay hiding suggestions to allow for selection
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  };

  return (
    <Form.Group className={`ai-input-group ${className}`}>
      {label && (
        <Form.Label 
          className="ai-label"
          style={{
            color: '#374151',
            fontWeight: 600,
            fontSize: '14px',
            marginBottom: '8px'
          }}
        >
          <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2" style={{ color: '#8b5cf6' }} />
          {label}
          {isRequired && <span className="text-danger"> *</span>}
        </Form.Label>
      )}
      
      <div className="position-relative" ref={containerRef}>
        <InputGroup>
          <Form.Control
            type="text"
            placeholder={placeholder}
            value={value}
                         onChange={(e) => handleAddressChange(e.target.value)}
             onBlur={(e) => {
               e.target.style.borderColor = 'rgba(139, 92, 246, 0.3)';
               e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
               handleAddressBlur();
             }}
            disabled={disabled}
            isInvalid={!!error}
            className="border-0 rounded-start-3"
            style={{
              background: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(139, 92, 246, 0.3) !important',
              borderRadius: '8px',
              padding: '12px 16px',
              fontSize: '14px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s ease'
            }}
                         onFocus={(e) => {
               e.target.style.borderColor = '#8b5cf6';
               e.target.style.boxShadow = '0 0 0 3px rgba(139, 92, 246, 0.1)';
             }}
          />
          <InputGroup.Text 
            className="border-0 rounded-end-3"
            style={{
              background: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(139, 92, 246, 0.3) !important',
              borderLeft: 'none !important'
            }}
          >
            {isLoadingSuggestions ? (
              <Spinner size="sm" style={{ color: '#8b5cf6' }} />
            ) : disabled ? (
              <FontAwesomeIcon 
                icon={faShieldAlt} 
                className="text-success" 
                title="Address field is locked"
                style={{ filter: 'drop-shadow(0 0 4px rgba(16, 185, 129, 0.5))' }}
              />
            ) : (
              <FontAwesomeIcon icon={faSearch} style={{ color: '#8b5cf6' }} />
            )}
          </InputGroup.Text>
        </InputGroup>

                 {/* AI-Enhanced Address Suggestions Dropdown */}
         {showSuggestions && suggestions.length > 0 && (
           <div className="position-absolute w-100" style={{ zIndex: 1050 }}>
             <div
               className="w-100 mt-2 border-0 rounded-3 shadow-lg" 
               style={{ 
                 maxHeight: '200px', 
                 overflowY: 'auto',
                 background: 'rgba(255, 255, 255, 0.95)',
                 backdropFilter: 'blur(15px)',
                 border: '1px solid rgba(139, 92, 246, 0.2)',
                 borderRadius: '12px'
               }}
             >
               {suggestions.map((suggestion, index) => (
                 <div
                   key={index}
                   onClick={(e) => {
                     e.preventDefault();
                     e.stopPropagation();
                     handleAddressSelect(suggestion);
                   }}
                   className="d-flex justify-content-between align-items-center p-3"
                   style={{ 
                     borderBottom: index < suggestions.length - 1 ? '1px solid rgba(139, 92, 246, 0.1)' : 'none',
                     transition: 'all 0.2s',
                     cursor: 'pointer',
                     borderRadius: index === 0 ? '12px 12px 0 0' : index === suggestions.length - 1 ? '0 0 12px 12px' : '0'
                   }}
                   onMouseEnter={(e) => {
                     e.currentTarget.style.background = 'rgba(139, 92, 246, 0.05)';
                   }}
                   onMouseLeave={(e) => {
                     e.currentTarget.style.background = 'transparent';
                   }}
                 >
                   <span className="fw-medium" style={{ fontSize: '14px' }}>{suggestion.address}</span>
                   <Badge 
                     className="ms-2 rounded-pill"
                     style={{
                       background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                       color: 'white',
                       fontSize: '10px',
                       padding: '4px 8px'
                     }}
                   >
                     {suggestion.source}
                   </Badge>
                 </div>
               ))}
             </div>
           </div>
         )}

        {error && (
          <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
            <FontAwesomeIcon icon={faShieldAlt} className="me-1" />
            {error}
          </Form.Control.Feedback>
        )}
      </div>
    </Form.Group>
  );
};

export default AddressSearchComponent; 